import { useTranslation } from 'react-i18next';
import { useDeleteVenue } from '@openapi/venues/venues';
import { useSwitchVenue } from '@openapi/auth/auth';
import { useAuth } from '../../../hooks/useAuth';
import { MODAL_TYPE, useModals } from '../../../hooks/useModal';
import useNotify from '../../../hooks/useNotify';

/**
 * Shared venue delete/switch behavior for the card and table views.
 * `reloadData` is the list refetch to run after a successful delete.
 */
export default function useVenueActions(reloadData) {
    const { t } = useTranslation();
    const { setSwitchIntoVenue, setAccessToken } = useAuth();
    const { showModal } = useModals();
    const { notifyError, notifySuccess } = useNotify();

    const deleteVenueMutation = useDeleteVenue({
        mutation: {
            onSuccess: () => {
                notifySuccess(t('venues.notifications.deleted'));
                reloadData();
            },
            onError: () => {
                notifyError(t('venues.notifications.deleteError'));
            },
        },
    });

    const switchVenueMutation = useSwitchVenue({
        mutation: {
            onSuccess: (switchVenueData) => {
                const { token, ...otherUserData } = switchVenueData.user ?? {};
                if (otherUserData && Object.keys(otherUserData).length > 0) {
                    setSwitchIntoVenue(otherUserData);
                }
                if (token) {
                    setAccessToken(token);
                }
                notifySuccess(t('users.notifications.switchVenue'));
            },
            onError: (error) => {
                notifyError(error);
            },
        },
    });

    const confirmDelete = (row) => {
        showModal(MODAL_TYPE.CONFIRMATION, {
            title: t('confirmationModal.title'),
            content: t('confirmationModal.content'),
            confirm: () => deleteVenueMutation.mutate({ id: row.id }),
        });
    };

    const switchVenue = (id) => {
        switchVenueMutation.mutate({ venueId: id });
    };

    const isDeleting = (id) =>
        deleteVenueMutation.isPending && deleteVenueMutation.variables?.id === id;

    const isSwitching = (id) =>
        switchVenueMutation.isPending && switchVenueMutation.variables?.venueId === id;

    return { confirmDelete, switchVenue, isDeleting, isSwitching };
}
